/* eslint-disable indent */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Button, Text, Select } from '@chakra-ui/react';
import Locale from '../../../utils/translations';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';
import AddTagsModal from '../../common/AddTagsModal';
import TestList from './TestList';
import { Test, Template, Sample } from '../../../utils/interfaces';
import AddTranslationTestModal from './AddTranslationModal';

type FormState = {
	error: string;
	testName: string;
	templateData: { [key: string]: string };
	selectedTemplateId: string[];
	selectedSampleId: string;
	selectedTest: Test | null;
	selectedLanguages: string[];
};

const TestsPage: React.FC = () => {
	const [templates, setTemplates] = useState<Template[]>([]);
	const [tests, setTests] = useState<Test[]>([]);
	const [samples, setSamples] = useState<Sample[]>([]);
	const [testTypeFilter, setTestTypeFilter] = useState<'none' | 'translation' | 's2t'>('none');

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [isDuplicating, setIsDuplicating] = useState(false);

	const [calculationError, setCalculationError] = useState<{
		testId: string;
		message: string;
	} | null>(null);

	const [formState, setFormState] = useState<FormState>({
		error: '',
		testName: '',
		templateData: {},
		selectedTemplateId: [],
		selectedSampleId: '',
		selectedTest: null,
		selectedLanguages: [],
	});

	const [translationModal, setTranslationModal] = useState({
		isOpen: false,
		sample: null as Sample | null,
		name: '',
		templateIds: [] as string[],
		language: '',
		isCreating: false,
		error: '',
		bcapIds: {} as Record<string, string | null>,
	});

	const [page, setPage] = useState(0);
	const [limit] = useState(5);
	const [total, setTotal] = useState(0);

	const updateTranslationModal = useCallback(
		<K extends keyof typeof translationModal>(key: K, value: (typeof translationModal)[K]) => {
			setTranslationModal(prev => ({ ...prev, [key]: value }));
		},
		[],
	);

	const updateForm = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
		setFormState(prev => ({ ...prev, [key]: value }));
	}, []);

	const handleOpenTranslationModal = useCallback(
		(test: Test, templateRun: any) => {
			const sample = samples.find(s => s.sampleId === test.selectedSampleId) || null;

			setTranslationModal({
				isOpen: true,
				sample,
				name: test.name || '',
				templateIds: Array.isArray(test.selectedTemplateId) ? test.selectedTemplateId : [],
				language: '',
				isCreating: false,
				error: '',
				bcapIds: templateRun?.bcapSessionId
					? { [templateRun.templateId]: templateRun.bcapSessionId }
					: {},
			});
		},
		[samples],
	);
	const fetchTests = useCallback(
		async (pageNum = 1) => {
			const api = new BonfireAPI(ApiType.QaApi);
			try {
				let url = `${config.qa_api.url}/aiModelComparisonsTests/list?offset=${pageNum}&limit=${limit}`;
				if (testTypeFilter !== 'none') {
					url += `&type=${testTypeFilter}`;
				}
				const response = await api.get(url);
				const items = response?.items ?? response ?? [];
				setTests(Array.isArray(items) ? items : []);
				if (typeof response?.total === 'number') {
					setTotal(response.total);
				}
			} catch {
				setTests([]);
			}
		},
		[limit, testTypeFilter],
	);

	const handleAddTranslationTest = useCallback(async () => {
		const { name, templateIds, sample, language, bcapIds } = translationModal;

		if (!name || !templateIds.length || !sample?.sampleId || !language) {
			updateTranslationModal('error', 'Please fill all required fields');
			return;
		}

		const api = new BonfireAPI(ApiType.QaApi);
		updateTranslationModal('isCreating', true);
		updateTranslationModal('error', '');

		try {
			const url = `${config.qa_api.url}/aiModelComparisonsTests/add`;
			await api.post(url, {
				name,
				tags: [{ key: 'languages', value: language }],
				selectedTemplateId: templateIds,
				selectedTEmplateBcapIds: bcapIds,
				selectedSampleId: sample.sampleId,
				type: 'translation',
				historyOfRuns: [],
				parentTestId: undefined,
				translationBcapSessionId: bcapIds || null,
			});

			await fetchTests(page);
			updateTranslationModal('isOpen', false);
		} catch (err: any) {
			updateTranslationModal('error', err?.message || 'Failed to add translation test');
		} finally {
			updateTranslationModal('isCreating', false);
		}
	}, [translationModal, fetchTests, page, updateTranslationModal]);

	const fetchTemplates = useCallback(async () => {
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const url = `${config.qa_api.url}/aiModelComparisonsTemplates/list`;
			const response = await api.get(url);
			setTemplates(Array.isArray(response) ? response : []);
		} catch {
			setTemplates([]);
		}
	}, []);

	useEffect(() => {
		fetchTemplates();
		fetchTests(page);
	}, [fetchTemplates, fetchTests, page, testTypeFilter]);

	const handleDuplicateTest = useCallback(
		(test: Test) => {
			setIsDuplicating(true);
			setIsModalOpen(true);
			updateForm('testName', `Duplicated ${test.name}`);
			updateForm(
				'templateData',
				test.tags.reduce(
					(acc, tag) => ({ ...acc, [tag.key]: tag.value }),
					{} as Record<string, string>,
				),
			);
			updateForm(
				'selectedTemplateId',
				Array.isArray(test.selectedTemplateId) ? test.selectedTemplateId : [],
			);
			updateForm('selectedSampleId', test.selectedSampleId);
			updateForm('selectedTest', test);
		},
		[updateForm],
	);

	const handleRunSimulation = useCallback(async (testId: string, selectedSampleId?: string) => {
		const api = new BonfireAPI(ApiType.QaApi);
		setCalculationError(null);
		try {
			const url = `${config.qa_api.url}/aiModelComparisonsTests/startSimulation`;
			await api.post(url, {
				sessionId: testId,
				sampleId: selectedSampleId || null,
			});
		} catch {
			setCalculationError({
				testId,
				message:
					'Failed to calculate ground truth. Simulation not run yet, or not finished',
			});
		}
	}, []);

	const handleCalculateGroundTruth = useCallback(
		async (testId: string, selectedTemplate: string, selectedSample: string) => {
			const api = new BonfireAPI(ApiType.QaApi);
			setCalculationError(null);
			try {
				const url = `${config.qa_api.url}/aiModelComparisonsTests/stats`;
				const result = await api.post(url, {
					sessionId: testId,
					templateId: selectedTemplate,
					sampleId: selectedSample,
				});
				setTests(prev =>
					prev.map(t => {
						if (t.testId !== testId) {
							return t;
						}
						if (result?.sessionType === 's2t') {
							return { ...t, historyOfRuns: result?.stats ?? t.historyOfRuns };
						}
						if (result?.sessionType === 'translation') {
							return {
								...t,
								translationResults: result?.stats ?? t.translationResults,
							};
						}
						return t;
					}),
				);
			} catch {
				setCalculationError({
					testId,
					message:
						'Failed to calculate ground truth. Simulation not run yet, or not finished',
				});
			}
		},
		[],
	);

	const handleDeleteTest = useCallback(
		async (testId: string) => {
			const api = new BonfireAPI(ApiType.QaApi);
			try {
				const url = `${config.qa_api.url}/aiModelComparisonsTests/delete/${testId}`;
				await api.delete(url, {});
				await fetchTests(page);
			} catch {
				console.error('Failed to delete test');
			}
		},
		[fetchTests, page],
	);

	const fetchSamples = async () => {
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const url = `${config.qa_api.url}/aiModelComparisonsSamples/list`;
			const response = await api.get(url);
			setSamples(Array.isArray(response) ? response : []);
		} catch {
			setSamples([]);
		}
	};

	useEffect(() => {
		fetchSamples();
	}, []);

	const handleCreateTestRun = useCallback(async () => {
		if (
			!formState.testName ||
			!formState.selectedTemplateId.length ||
			!formState.selectedSampleId
		) {
			updateForm('error', 'Please fill all required fields');
			return;
		}

		const tags = Object.entries(formState.templateData)
			.filter(([, value]) => value !== undefined && value !== '')
			.map(([key, value]) => ({ key, value }));

		const selectedSample = samples.find(s => s.sampleId === formState.selectedSampleId);
		const audioFile = {
			key: 'audioFile',
			value: selectedSample?.audioFileUrl
				? selectedSample.audioFileUrl
				: `${config.qa_api.url}/files/getFile?type=audio-files&id=${formState.selectedSampleId}_aiexperiments.wav`,
		};
		tags.push(audioFile);

		const api = new BonfireAPI(ApiType.QaApi);
		setIsCreating(true);
		updateForm('error', '');

		try {
			const url = `${config.qa_api.url}/aiModelComparisonsTests/add`;
			await api.post(url, {
				name: formState.testName,
				tags,
				selectedTemplateId: formState.selectedTemplateId,
				selectedSampleId: formState.selectedSampleId,
				historyOfRuns: formState.selectedTest?.historyOfRuns,
				parentTestId:
					isDuplicating && formState.selectedTest
						? formState.selectedTest.testId
						: undefined,
			});

			await fetchTests(page);
			setIsModalOpen(false);
			setIsDuplicating(false);
			setFormState({
				error: '',
				testName: '',
				templateData: {},
				selectedTemplateId: [],
				selectedSampleId: '',
				selectedTest: null,
				selectedLanguages: [],
			});
		} catch (err: any) {
			updateForm('error', err?.message || 'Failed to add template');
		} finally {
			setIsCreating(false);
		}
	}, [fetchTests, formState, isDuplicating, page, samples, updateForm]);

	const pageCount = useMemo(() => Math.max(1, Math.ceil((total || 0) / limit)), [total, limit]);

	return (
		<Box p={6}>
			<Flex mb={6} alignItems="center" justifyContent="space-between">
				<Flex>
					<Text fontSize="2xl" fontWeight="bold">
						{Locale.get('Test simulation')}
					</Text>
					<Select
						bg={'white'}
						width="180px"
						ml={4}
						value={testTypeFilter}
						onChange={e =>
							setTestTypeFilter(e.target.value as 'none' | 'translation' | 's2t')
						}
					>
						<option value="none">All types</option>
						<option value="translation">Translation</option>
						<option value="s2t">S2T</option>
					</Select>
					<Flex justify="center" align="center" gap={2} ml={10}>
						<Button
							size="sm"
							onClick={() => setPage(p => Math.max(0, p - 1))}
							isDisabled={page === 0}
						>
							Previous
						</Button>
						<Text>
							Page {page + 1} of {pageCount}
						</Text>
						<Button
							size="sm"
							onClick={() => setPage(p => p + 1)}
							isDisabled={page >= pageCount - 1}
						>
							Next
						</Button>
					</Flex>
				</Flex>

				<Button variant="bePrimary" onClick={() => setIsModalOpen(true)}>
					{Locale.get('Create a test run')}
				</Button>
			</Flex>

			<AddTagsModal
				section="tests"
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setIsDuplicating(false);
					updateForm('selectedTest', null);
				}}
				templateName={formState.testName}
				setTemplateName={name => updateForm('testName', name)}
				templateData={formState.templateData}
				setTemplateData={data =>
					updateForm(
						'templateData',
						typeof data === 'function' ? data({ ...formState.templateData }) : data,
					)
				}
				setError={msg => updateForm('error', msg)}
				TEMPLATE_FIELDS={[]}
				samples={samples}
				selectedSampleId={formState.selectedSampleId}
				setSelectedSampleId={id => updateForm('selectedSampleId', id)}
				isCreating={isCreating}
				error={formState.error}
				onAddTemplate={handleCreateTestRun}
				selectedTemplateId={formState.selectedTemplateId}
				setSelectedTemplateId={ids => updateForm('selectedTemplateId', ids)}
				templates={templates}
				isDuplicating={isDuplicating}
			/>

			{translationModal.isOpen && (
				<AddTranslationTestModal
					isOpen={translationModal.isOpen}
					onClose={() => updateTranslationModal('isOpen', false)}
					sample={translationModal.sample}
					name={translationModal.name}
					setName={name => updateTranslationModal('name', name)}
					selectedTemplateIds={translationModal.templateIds}
					setSelectedTemplateIds={ids => updateTranslationModal('templateIds', ids)}
					selectedTemplateBcapIds={translationModal.bcapIds}
					translationLanguages={translationModal.language}
					setTranslationLanguages={lang => updateTranslationModal('language', lang)}
					isCreating={translationModal.isCreating}
					error={translationModal.error}
					onAdd={handleAddTranslationTest}
				/>
			)}

			<Box mt={10}>
				<Text fontSize="xl" fontWeight="bold" mb={3}>
					{Locale.get('Created test runs')}
				</Text>
				<TestList
					tests={tests}
					templates={templates}
					onRunSimulation={handleRunSimulation}
					onCalculateGroundTruth={handleCalculateGroundTruth}
					calculationError={calculationError}
					handleDuplicateTest={handleDuplicateTest}
					handleDeleteTest={handleDeleteTest}
					onOpenTranslationModal={handleOpenTranslationModal}
				/>
			</Box>
		</Box>
	);
};

export default TestsPage;
